/* GreenFox Product Page Interactions */

/* Gallery thumbnail switching */
document.addEventListener('click', (e) => {
  const thumb = e.target.closest('[data-thumb-index]');
  if (!thumb) return;

  const gallery = thumb.closest('.gf-gallery');
  if (!gallery) return;

  const index = parseInt(thumb.dataset.thumbIndex, 10);
  const images = gallery.querySelectorAll('.gf-gallery__main-image');
  const thumbs = gallery.querySelectorAll('.gf-gallery__thumb');

  images.forEach((img, i) => {
    img.hidden = i !== index;
  });

  thumbs.forEach((t, i) => {
    t.classList.toggle('gf-gallery__thumb--active', i === index);
    t.setAttribute('aria-current', i === index ? 'true' : 'false');
  });
});

/* Quantity +/- buttons */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
  if (!btn) return;

  const wrapper = btn.closest('.gf-quantity');
  if (!wrapper) return;

  const input = wrapper.querySelector('.gf-quantity__input');
  if (!input) return;

  const min = parseInt(input.min, 10) || 1;
  const max = parseInt(input.max, 10) || 9999;
  const step = parseInt(input.step, 10) || 1;
  let value = parseInt(input.value, 10) || min;

  if (btn.hasAttribute('data-qty-minus')) {
    value = Math.max(min, value - step);
  } else {
    value = Math.min(max, value + step);
  }

  input.value = value;
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* Variant selector -- wires option radios to variant ID, price, availability */
class GfVariantSelector extends HTMLElement {
  connectedCallback() {
    const section = this.closest('[data-section-id]');
    if (!section) return;

    const sectionId = section.dataset.sectionId;
    const jsonEl = document.getElementById(`gf-product-variants-${sectionId}`);
    if (!jsonEl) return;

    try {
      this.variants = JSON.parse(jsonEl.textContent);
    } catch {
      return;
    }
    this.section = section;
    this.sectionId = sectionId;

    this.variantInput = section.querySelector('input.product-variant-id');
    this.priceWrapper = section.querySelector('[data-product-price]');
    this.unitLabel = this.priceWrapper?.querySelector('.gf-card__price-unit')?.cloneNode(true) || null;
    this.atcBtn = section.querySelector('.gf-product__atc-btn');
    this.stickyPrice = document.querySelector('[data-sticky-price]');
    this.optionInputs = section.querySelectorAll('.gf-swatch-option__input, .gf-size-option__input');

    /* Cache original ATC text immediately */
    if (this.atcBtn) {
      const textSpan = this.atcBtn.querySelector('span:first-child');
      const soldOutMsg = this.atcBtn.querySelector('.sold-out-message');
      if (textSpan && soldOutMsg) {
        this.addToCartText = soldOutMsg.dataset.addText || textSpan.textContent.trim();
        this.soldOutText = soldOutMsg.textContent.trim();
      }
    }

    this._changeHandlers = [];
    this.optionInputs.forEach((input) => {
      const handler = () => this.onOptionChange();
      input.addEventListener('change', handler);
      this._changeHandlers.push({ input, handler });
    });
  }

  disconnectedCallback() {
    if (this._changeHandlers) {
      this._changeHandlers.forEach(({ input, handler }) => {
        input.removeEventListener('change', handler);
      });
    }
  }

  onOptionChange() {
    const selectedOptions = [];
    const optionGroups = this.section.querySelectorAll('.gf-variant-group');

    optionGroups.forEach((group) => {
      const checked = group.querySelector('input:checked');
      if (checked) selectedOptions.push(checked.value);
    });

    /*
     * Wholesale mode: products must have color-only option.
     * Match only the rendered/selected options against variant.options.
     * If wholesale product incorrectly has multiple options, match first N only.
     */
    const isWholesale = this.section.dataset.wholesale === 'true';
    const variant = this.variants.find((v) => {
      if (isWholesale && selectedOptions.length < v.options.length) {
        return selectedOptions.every((opt, i) => v.options[i] === opt);
      }
      return v.options.length === selectedOptions.length &&
        v.options.every((opt, i) => opt === selectedOptions[i]);
    });

    if (!variant) {
      this.disableForm();
      return;
    }

    this.updateVariantId(variant);
    this.updatePrice(variant);
    this.updateAvailability(variant);
    this.updateOptionLabels(selectedOptions);
    this.updateUrl(variant);
    this.updateOptionStates(selectedOptions);
    this.updateGallery(variant);
    this.updateQuantityRules(variant);
  }

  updateVariantId(variant) {
    if (this.variantInput) {
      this.variantInput.value = variant.id;
      this.variantInput.setAttribute('value', variant.id);
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updatePrice(variant) {
    if (!this.priceWrapper) return;

    const price = this.formatMoney(variant.price);
    const comparePrice = variant.compare_at_price;

    /* Clear existing price elements safely */
    while (this.priceWrapper.firstChild) {
      this.priceWrapper.removeChild(this.priceWrapper.firstChild);
    }

    if (comparePrice && comparePrice > variant.price) {
      const savings = Math.round(((comparePrice - variant.price) / comparePrice) * 100);

      const compareEl = document.createElement('s');
      compareEl.className = 'gf-product__price-compare';
      compareEl.textContent = this.formatMoney(comparePrice);
      this.priceWrapper.appendChild(compareEl);

      const priceEl = document.createElement('span');
      priceEl.className = 'gf-product__price gf-product__price--sale';
      priceEl.textContent = price;
      this.priceWrapper.appendChild(priceEl);

      const badgeEl = document.createElement('span');
      badgeEl.className = 'gf-product__price-badge';
      badgeEl.textContent = `-${savings}%`;
      this.priceWrapper.appendChild(badgeEl);
    } else {
      const priceEl = document.createElement('span');
      priceEl.className = 'gf-product__price';
      priceEl.textContent = price;
      this.priceWrapper.appendChild(priceEl);
    }

    /* Preserve wholesale unit label if present */
    if (this.unitLabel) {
      this.priceWrapper.appendChild(this.unitLabel);
    }

    if (this.stickyPrice) {
      const stickyUnit = this.stickyPrice.dataset.unit || '';
      this.stickyPrice.textContent = stickyUnit ? `${price} ${stickyUnit}` : price;
    }
  }

  updateAvailability(variant) {
    if (!this.atcBtn) return;

    const textSpan = this.atcBtn.querySelector('span:first-child');
    const dynamicCheckoutWrapper = this.section.querySelector('.shopify-payment-button')?.parentElement;

    if (variant.available) {
      this.atcBtn.disabled = false;
      this.atcBtn.removeAttribute('aria-disabled');
      if (dynamicCheckoutWrapper) dynamicCheckoutWrapper.style.display = '';
      if (textSpan && this.addToCartText) {
        textSpan.textContent = this.addToCartText;
      }
    } else {
      this.atcBtn.disabled = true;
      this.atcBtn.setAttribute('aria-disabled', 'true');
      if (dynamicCheckoutWrapper) dynamicCheckoutWrapper.style.display = 'none';
      if (textSpan && this.soldOutText) {
        textSpan.textContent = this.soldOutText;
      }
    }
  }

  updateOptionLabels(selectedOptions) {
    const valueSpans = this.section.querySelectorAll('[data-option-value]');
    valueSpans.forEach((span) => {
      const pos = parseInt(span.dataset.optionValue, 10) - 1;
      if (selectedOptions[pos] !== undefined) {
        span.textContent = selectedOptions[pos];
      }
    });
  }

  updateQuantityRules(variant) {
    const qtyInput = document.querySelector(`#gf-quantity-${this.sectionId}`);
    if (!qtyInput) return;

    const rule = variant.quantity_rule || {};
    const min = rule.min || 1;
    const max = rule.max;
    const step = rule.increment || 1;

    qtyInput.min = min;
    if (max) {
      qtyInput.max = max;
    } else {
      qtyInput.removeAttribute('max');
    }
    qtyInput.step = step;

    const current = parseInt(qtyInput.value, 10) || min;
    if (current < min) qtyInput.value = min;
    if (max && current > max) qtyInput.value = max;
  }

  updateGallery(variant) {
    if (!variant.featured_media) return;
    const gallery = this.section.querySelector('.gf-gallery');
    if (!gallery) return;

    const images = gallery.querySelectorAll('.gf-gallery__main-image');
    const thumbs = gallery.querySelectorAll('.gf-gallery__thumb');
    images.forEach((img, i) => {
      img.hidden = true;
    });
    thumbs.forEach((t) => {
      t.classList.remove('gf-gallery__thumb--active');
      t.setAttribute('aria-current', 'false');
    });

    /* Find matching media by position (variant.featured_media.position is 1-based) */
    const targetIndex = variant.featured_media.position - 1;
    if (images[targetIndex]) images[targetIndex].hidden = false;
    if (thumbs[targetIndex]) {
      thumbs[targetIndex].classList.add('gf-gallery__thumb--active');
      thumbs[targetIndex].setAttribute('aria-current', 'true');
      thumbs[targetIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  updateUrl(variant) {
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateOptionStates(selectedOptions) {
    /* Dawn approach: never disable options. Mark unavailable visually but keep selectable. */
    const optionGroups = [...this.section.querySelectorAll('.gf-variant-group')];
    optionGroups.forEach((group, groupIdx) => {
      const inputs = group.querySelectorAll('input[type="radio"]');
      inputs.forEach((input) => {
        input.disabled = false;
        const testOptions = [...selectedOptions];
        testOptions[groupIdx] = input.value;
        const matchingVariant = this.variants.find((v) =>
          v.options.every((opt, i) => opt === testOptions[i])
        );
        const isUnavailable = !matchingVariant || !matchingVariant.available;
        const label = input.nextElementSibling;
        if (label) label.classList.toggle('gf-option--unavailable', isUnavailable);
      });
    });
  }

  disableForm() {
    if (this.variantInput) {
      this.variantInput.value = '';
    }
    if (this.atcBtn) {
      this.atcBtn.disabled = true;
      this.atcBtn.setAttribute('aria-disabled', 'true');
      const textSpan = this.atcBtn.querySelector('span:first-child');
      if (textSpan) textSpan.textContent = this.soldOutText || textSpan.textContent;
    }
    if (this.stickyPrice) {
      this.stickyPrice.textContent = '';
    }
    const dcWrapper = this.section.querySelector('.shopify-payment-button')?.parentElement;
    if (dcWrapper) dcWrapper.style.display = 'none';
  }

  formatMoney(cents) {
    const moneyFormat = this.section.dataset.moneyFormat || '{{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(cents / 100));
    const amountComma = amount.replace('.', ',');
    const amountDot = amount.replace(/(\d)(?=(\d{3})+\.)/g, '$1.');

    let formatted = moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals.replace(/(\d)(?=(\d{3})+$)/g, '$1.'))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amount.replace(/(\d)(?=(\d{3})+\.)/g, "$1'"))
      .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, amountNoDecimals.replace(/(\d)(?=(\d{3})+$)/g, '$1 '))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);

    const tmp = document.createElement('span');
    tmp.textContent = formatted;
    return tmp.textContent;
  }
}

if (!customElements.get('gf-variant-selector')) customElements.define('gf-variant-selector', GfVariantSelector);

/* Product tabs (desktop: tab switching with arrow keys, mobile: accordion) */
class GfProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabBtns = [...this.querySelectorAll('.gf-tabs__nav-btn')];
    this.panels = [...this.querySelectorAll('.gf-tabs__panel')];
    this.toggleBtns = this.querySelectorAll('.gf-tabs__panel-toggle');

    this.tabBtns.forEach((btn, i) => {
      btn.setAttribute('tabindex', i === 0 ? '0' : '-1');
      btn.addEventListener('click', () => this.switchTab(btn));
      btn.addEventListener('keydown', (e) => this.handleTabKeydown(e, btn));
    });

    this.toggleBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.toggleAccordion(btn));
    });
  }

  switchTab(activeBtn) {
    const targetId = activeBtn.getAttribute('aria-controls');

    this.tabBtns.forEach((btn) => {
      const isActive = btn === activeBtn;
      btn.classList.toggle('gf-tabs__nav-btn--active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
      btn.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panels.forEach((panel) => {
      const isActive = panel.id === targetId;
      panel.classList.toggle('gf-tabs__panel--active', isActive);
      const content = panel.querySelector('.gf-tabs__panel-content');
      if (content) {
        content.setAttribute('aria-hidden', String(!isActive));
        content.classList.toggle('gf-tabs__panel-content--open', isActive);
      }
      const toggle = panel.querySelector('.gf-tabs__panel-toggle');
      if (toggle) toggle.setAttribute('aria-expanded', String(isActive));
    });
  }

  handleTabKeydown(e, currentBtn) {
    const idx = this.tabBtns.indexOf(currentBtn);
    let nextIdx;

    if (e.key === 'ArrowRight') {
      nextIdx = (idx + 1) % this.tabBtns.length;
    } else if (e.key === 'ArrowLeft') {
      nextIdx = (idx - 1 + this.tabBtns.length) % this.tabBtns.length;
    } else if (e.key === 'Home') {
      nextIdx = 0;
    } else if (e.key === 'End') {
      nextIdx = this.tabBtns.length - 1;
    } else {
      return;
    }

    e.preventDefault();
    this.tabBtns[nextIdx].focus();
    this.switchTab(this.tabBtns[nextIdx]);
  }

  toggleAccordion(btn) {
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    const contentId = btn.getAttribute('aria-controls');
    const content = this.querySelector(`#${contentId}`);
    const panel = btn.closest('.gf-tabs__panel');

    btn.setAttribute('aria-expanded', String(!expanded));
    if (content) {
      content.classList.toggle('gf-tabs__panel-content--open', !expanded);
      content.setAttribute('aria-hidden', String(expanded));
    }
    if (panel) {
      panel.removeAttribute('aria-hidden');
    }
  }
}

if (!customElements.get('gf-product-tabs')) customElements.define('gf-product-tabs', GfProductTabs);

/* Sticky add-to-cart (IntersectionObserver + variant availability mirror) */
class GfStickyAtc extends HTMLElement {
  connectedCallback() {
    this.primaryBtn = document.querySelector('.gf-product__atc-btn');
    this.stickyBtn = this.querySelector('[data-sticky-atc-btn]');
    if (!this.primaryBtn) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.classList.toggle('gf-sticky-atc--visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );
    this.observer.observe(this.primaryBtn);

    if (this.stickyBtn) {
      this.stickyBtn.addEventListener('click', () => {
        if (!this.primaryBtn.disabled) this.primaryBtn.click();
      });
    }

    this.mirrorObserver = new MutationObserver(() => this.mirrorState());
    this.mirrorObserver.observe(this.primaryBtn, { attributes: true, attributeFilter: ['disabled'] });
  }

  mirrorState() {
    if (!this.stickyBtn || !this.primaryBtn) return;
    this.stickyBtn.disabled = this.primaryBtn.disabled;
    const primaryText = this.primaryBtn.querySelector('span:first-child');
    if (primaryText) this.stickyBtn.textContent = primaryText.textContent;
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.mirrorObserver) this.mirrorObserver.disconnect();
  }
}

if (!customElements.get('gf-sticky-atc')) customElements.define('gf-sticky-atc', GfStickyAtc);
